
import { defineComponent, type PropType } from "vue";

export type ScrollbarDirection = "Horizontal" | "Vertical";

// Linear Interpolation
const lerp = (x: number, y: number, a: number): number => x * (1 - a) + y * a;

// Convert the position of the handle (0-1) to the position on the track (0-1).
// This includes the 1/2 handle length gap of the possible handle positionson each side so the end of the handle doesn't go off the track.
const handleToTrack = (handleLen: number, handlePos: number): number => lerp(handleLen / 2, 1 - handleLen / 2, handlePos);

const pointerPosition = (direction: ScrollbarDirection, e: PointerEvent): number => (direction === "Vertical" ? e.clientY : e.clientX);

export default defineComponent({
	emits: {
		"update:handlePosition": null,
		pressTrack: (pointerOffset: number) => typeof pointerOffset === "number",
	},
	props: {
		direction: { type: String as PropType<ScrollbarDirection>, default: "Vertical" },
		handlePosition: { type: Number as PropType<number>, default: 0.5 },
		handleLength: { type: Number as PropType<number>, default: 0.5 },
	},
	computed: {
		thumbStart(): { left: string } | { top: string } {
			const start = handleToTrack(this.handleLength, this.handlePosition) - this.handleLength / 2;

			return this.direction === "Vertical" ? { top: `${start * 100}%` } : { left: `${start * 100}%` };
		},
		thumbEnd(): { right: string } | { bottom: string } {
			const end = 1 - handleToTrack(this.handleLength, this.handlePosition) - this.handleLength / 2;

			return this.direction === "Vertical" ? { bottom: `${end * 100}%` } : { right: `${end * 100}%` };
		},
		sides(): { left: string; right: string } | { top: string; bottom: string } {
			return this.direction === "Vertical" ? { left: "0%", right: "0%" } : { top: "0%", bottom: "0%" };
		},
	},
	data() {
		return {
			dragging: false,
			pointerPos: 0,
		};
	},
	mounted() {
		window.addEventListener("pointerup", this.pointerUp);
		window.addEventListener("pointermove", this.pointerMove);
	},
	unmounted() {
		window.removeEventListener("pointerup", this.pointerUp);
		window.removeEventListener("pointermove", this.pointerMove);
	},
	methods: {
		trackLength(): number {
			const track = this.$refs.scrollTrack as HTMLElement;
			return this.direction === "Vertical" ? track.clientHeight - this.handleLength : track.clientWidth;
		},
		trackOffset(): number {
			const track = this.$refs.scrollTrack as HTMLElement;
			return this.direction === "Vertical" ? track.getBoundingClientRect().top : track.getBoundingClientRect().left;
		},
		clampHandlePosition(newPos: number) {
			const clampedPosition = Math.min(Math.max(newPos, 0), 1);
			this.$emit("update:handlePosition", clampedPosition);
		},
		updateHandlePosition(e: PointerEvent) {
			const position = pointerPosition(this.direction, e);
			this.clampHandlePosition(this.handlePosition + (position - this.pointerPos) / (this.trackLength() * (1 - this.handleLength)));
			this.pointerPos = position;
		},
		grabHandle(e: PointerEvent) {
			if (!this.dragging) {
				this.dragging = true;
				this.pointerPos = pointerPosition(this.direction, e);
			}
		},
		grabArea(e: PointerEvent) {
			if (!this.dragging) {
				const pointerPos = pointerPosition(this.direction, e);
				const oldPointer = handleToTrack(this.handleLength, this.handlePosition) * this.trackLength() + this.trackOffset();
				this.$emit("pressTrack", pointerPos - oldPointer);
			}
		},
		pointerUp() {
			this.dragging = false;
		},
		pointerMove(e: PointerEvent) {
			if (this.dragging) this.updateHandlePosition(e);
		},
		changePosition(difference: number) {
			this.clampHandlePosition(this.handlePosition + difference / this.trackLength());
		},
	},
});
